import { useCallback, useEffect } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { TruckGatepassFormData } from "@features/gatepass/schema/truckGatepassFormSchema";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { t } from "i18next";
import { FormProvider, useFormState } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useTruckGatepassFormStore } from "../../store/useTruckGatepassForm.store";
import type { TruckGatepassOutletContext } from "./types";

const TRANSLATION_KEY = "gatepass";
const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"];
const JOB_MODE_DEPENDENT_FIELDS = [
  "destinationVcn",
  "vesselName",
  "serviceNumber",
  "jobNumber",
  "jobDescription",
  "jobStartDate",
  "jobEndDate",
] as const;

const TruckGatepassDetails = () => {
  const { formData, updateFormData } = useOutletContext<TruckGatepassOutletContext>();
  const { submitCount } = useFormState({ control: formData.control });
  const { isFottDisabled } = useTruckGatepassFormStore();
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const isIndividualRole = INDIVIDUAL_ROLES.some((role) => userRoles.includes(role));

  const selectedTruckType = formData.watch("truckType");
  const selectedRequestFor = formData.watch("requestFor");
  const selectedEntryType = formData.watch("entryType");
  const selectedGatepassDuration = formData.watch("gatepassDuration");
  const selectedFottEntry = formData.watch("FOTTEntry");
  const isFottEntryDisabled = isFottDisabled;
  const isTruckType =
    String(selectedTruckType ?? "")
      .trim()
      .toUpperCase() === "TRUCK";
  const isCargoType =
    String(selectedTruckType ?? "")
      .trim()
      .toUpperCase() === "CARGO";
  const isTruckOrCargoType = isTruckType || isCargoType;

  useEffect(() => {
    formData.setValue("isFottDisabled", isFottDisabled, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [formData, isFottDisabled]);

  useEffect(() => {
    if (isFottDisabled && selectedFottEntry !== false) {
      formData.setValue("FOTTEntry", false, {
        shouldDirty: true,
        shouldValidate: false,
      });
      updateFormData("FOTTEntry", false);
    }
  }, [formData, isFottDisabled, selectedFottEntry, updateFormData]);

  useEffect(() => {
    if (selectedGatepassDuration === "permanent") {
      formData.setValue("gatepassDuration", "daily", {
        shouldDirty: true,
        shouldValidate: false,
      });
      formData.clearErrors("gatepassDuration");
      formData.clearErrors("permanentDuration");
      updateFormData("gatepassDuration", "daily");
    }
  }, [formData, selectedGatepassDuration, updateFormData]);

  useEffect(() => {
    if (!isTruckOrCargoType || selectedRequestFor === "entry") return;

    formData.setValue("requestFor", "entry", {
      shouldDirty: true,
      shouldValidate: true,
    });
    formData.clearErrors("requestFor");
    updateFormData("requestFor", "entry");
  }, [formData, isTruckOrCargoType, selectedRequestFor, updateFormData]);

  useEffect(() => {
    if (!isCargoType) return;

    if (selectedEntryType !== "single") {
      formData.setValue("entryType", "single", {
        shouldDirty: true,
        shouldValidate: true,
      });
      formData.clearErrors("entryType");
      updateFormData("entryType", "single");
    }

    if (selectedGatepassDuration !== "daily") {
      formData.setValue("gatepassDuration", "daily", {
        shouldDirty: true,
        shouldValidate: true,
      });
      formData.clearErrors("gatepassDuration");
      updateFormData("gatepassDuration", "daily");
    }
  }, [formData, isCargoType, selectedEntryType, selectedGatepassDuration, updateFormData]);

  const gatepassDurationOptions = [
    { label: t("common.daily"), value: "daily" },
    { label: t("common.weekly"), value: "weekly" },
  ];

  const entryTypeOptions = [
    { label: t("common.single"), value: "single" },
    { label: t("common.multiple"), value: "multiple" },
  ];

  const requestForOptions = [
    { label: t("common.entry"), value: "entry" },
    { label: t("common.exit"), value: "exit" },
  ];

  const fottEntryOptions = [
    { label: t("common.yes"), value: true },
    { label: t("common.no"), value: false },
  ];

  const truckTypeOptions = [
    { label: t(`${TRANSLATION_KEY}.truck-type-truck`), value: "TRUCK" },
    { label: t(`${TRANSLATION_KEY}.cargo`), value: "CARGO" },
  ];

  const cargoDestinationOptions = [
    { label: t("common.port"), value: "port" },
    { label: t(`${TRANSLATION_KEY}.vessel`), value: "vessel" },
  ];

  const handleTruckModeFieldChange = useCallback(
    <K extends "truckType" | "cargoDestination">(key: K, value: TruckGatepassFormData[K]) => {
      updateFormData(key, value);
      formData.clearErrors(JOB_MODE_DEPENDENT_FIELDS);

      if (submitCount > 0) {
        void formData.trigger(JOB_MODE_DEPENDENT_FIELDS);
      }
    },
    [formData, submitCount, updateFormData],
  );

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${TRANSLATION_KEY}.gatepass-details`)}>
        <div className="col-span-full flex">
          <FormItem<TruckGatepassFormData, "truckType">
            name="truckType"
            label={t(`${TRANSLATION_KEY}.truck-cargo-mode`)}
            type="radio"
            options={truckTypeOptions}
            testId="truck-radio-truckType"
            onFieldChange={handleTruckModeFieldChange}
            horizontalRadioButtons
            inlineRadioButtons
            radioValidationBelowButtons
          />
        </div>

        {!isIndividualRole && (
          <>
            <div className="col-span-full flex">
              <FormItem<TruckGatepassFormData, "entryType">
                name="entryType"
                label={t(`${TRANSLATION_KEY}.entry-type`)}
                type="radio"
                options={entryTypeOptions}
                testId="truck-radio-entryType"
                onFieldChange={updateFormData}
                horizontalRadioButtons
                inlineRadioButtons
                radioValidationBelowButtons
                disabled={isCargoType}
              />
            </div>
            <div className="col-span-full flex">
              <FormItem<TruckGatepassFormData, "requestFor">
                name="requestFor"
                label={t(`${TRANSLATION_KEY}.request-for`)}
                type="radio"
                options={requestForOptions}
                testId="truck-radio-requestFor"
                onFieldChange={updateFormData}
                horizontalRadioButtons
                inlineRadioButtons
                radioValidationBelowButtons
                disabled={isTruckOrCargoType}
              />
            </div>
          </>
        )}
        {!isTruckType && (
          <div className="col-span-full flex">
            <FormItem<TruckGatepassFormData, "gatepassDuration">
              name="gatepassDuration"
              label={t(`${TRANSLATION_KEY}.gatepass-duration`)}
              type="radio"
              options={gatepassDurationOptions}
              testId="truck-radio-gatepassDuration"
              onFieldChange={updateFormData}
              horizontalRadioButtons
              inlineRadioButtons
              radioValidationBelowButtons
              disabled={isCargoType}
            />
          </div>
        )}

        <div className="col-span-full flex">
          <FormItem<TruckGatepassFormData, "FOTTEntry">
            name="FOTTEntry"
            label={t(`${TRANSLATION_KEY}.fott-entry`)}
            type="radio"
            options={fottEntryOptions}
            testId="truck-radio-FOTTEntry"
            onFieldChange={(key, value) => {
              updateFormData(key, value);
              if (value === true || value === false) {
                formData.clearErrors("FOTTEntry");
              }
            }}
            horizontalRadioButtons
            inlineRadioButtons
            radioValidationBelowButtons
            disabled={isFottEntryDisabled}
          />
        </div>

        <div className="col-span-full flex">
          <FormItem<TruckGatepassFormData, "cargoDestination">
            name="cargoDestination"
            label={t(`${TRANSLATION_KEY}.cargo-destination`)}
            type="radio"
            options={cargoDestinationOptions}
            testId="truck-radio-cargoDestination"
            onFieldChange={handleTruckModeFieldChange}
            horizontalRadioButtons
            inlineRadioButtons
            radioValidationBelowButtons
          />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default TruckGatepassDetails;
